import React, { useEffect, useState } from "react";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  ShopBreadcrumbsProvider,
  useShopBreadcrumbs,
} from "@/integrations/wordpress/WordPressEcommerceShopBreadcrumbs";
import {
  EcommerceTermsProvider,
  useEcommerceTerms,
} from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
import { Link } from "@/components/common/Link";


declare const wvcClient: {
  getCurrentWPTermQueryParams: () => Record<string, any>;
};

function PageHeaderContent() {
  const { links, loading: breadcrumbsLoading } = useShopBreadcrumbs();
  const { terms, loading: termsLoading } = useEcommerceTerms();

  
  const currentBreadcrumb = links.length > 0 ? links[links.length - 1] : null;
  const pageTitle = currentBreadcrumb ? currentBreadcrumb.title : "Shop";

  
  
  const currentTerm = terms.find(
    (t) => t.name.toLowerCase() === pageTitle.toLowerCase()
  ) || (terms.length === 1 ? terms[0] : null);

  const isLoading = breadcrumbsLoading;

  if (isLoading) {
    return (
      <div className="w-full space-y-4 animate-pulse">
        <div className="h-4 w-32 bg-muted rounded-md" />
        <div className="h-10 w-64 bg-muted rounded-md" />
        <div className="h-4 w-96 bg-muted rounded-md" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {}
      {links.length > 0 && (
        <Breadcrumb>
          <BreadcrumbList>
            {links.map((link, index) => {
              const isLast = index === links.length - 1;
              return (
                <React.Fragment key={link.href}>
                  <BreadcrumbItem>
                    {isLast ? (
                      <BreadcrumbPage className="font-medium text-foreground">
                        {link.title}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild>
                        <Link to={link.href} className="text-muted-foreground hover:text-primary transition-colors">
                          {link.title}
                        </Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                  {!isLast && <BreadcrumbSeparator />}
                </React.Fragment>
              );
            })}
          </BreadcrumbList>
        </Breadcrumb>
      )}

      {}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
          {pageTitle}
        </h1>
        {currentTerm?.description && (
          <p className="max-w-3xl text-lg text-muted-foreground leading-relaxed">
            {currentTerm.description}
          </p>
        )}
      </div>
    </div>
  );
}

export default function PageTitleAndBreadcrumbs() {
  
  
  const [wpTermQuery, setWpTermQuery] = useState<WP_Term_Query | null>(null);

  useEffect(() => {
    if (typeof wvcClient !== "undefined") {
      const params = wvcClient.getCurrentWPTermQueryParams();
      setWpTermQuery(new WP_Term_Query(params));
    }
  }, []);

  if (!wpTermQuery) {
    return null; 
  }

  return (
    <section id="page-title-and-breadcrumbs" className="bg-background py-12 md:py-16 border-b border-border">
      <div className="container mx-auto px-4 md:px-6">
        <ShopBreadcrumbsProvider>
          <EcommerceTermsProvider wp_term_query={wpTermQuery}>
            <PageHeaderContent />
          </EcommerceTermsProvider>
        </ShopBreadcrumbsProvider>
      </div>
    </section>
  );
}